import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import PageLayout, { PageSection } from '../../components/navigation/PageLayout';
import CalculationSummary from '../tronc-distribution/components/CalculationSummary';
import EmployeeDistributionList from '../tronc-distribution/components/EmployeeDistributionList';
import Icon from '../../components/AppIcon';
import { useEmployees } from '../../hooks/useEmployees';
import { useTroncRecords } from '../../hooks/useTroncRecords';
import { useDistributionRules, useDistributionRuleMutations } from '../../hooks/useDistributionRules';
import { useLocationContext } from '../../contexts/LocationContext';
import { useTenantContext } from '../../contexts/TenantContext';

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount || 0);
};

const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const getDistributionTypeInfo = (type) => {
    const typeMap = {
        'Equal': { name: 'Equal Split', icon: 'Users', color: 'text-blue-600', bgColor: 'bg-blue-100' },
'ByJobRole': { name: 'By Job Role', icon: 'PieChart', color: 'text-purple-600', bgColor: 'bg-purple-100' },
        'ByJobRoleScore': { name: 'Job Role Score', icon: 'Trophy', color: 'text-amber-600', bgColor: 'bg-amber-100' },
        'ByDepartment': { name: 'By Department', icon: 'Building2', color: 'text-emerald-600', bgColor: 'bg-emerald-100' },
 'ByHoursAndJobRole': { name: 'Hours & Job Role', icon: 'Clock', color: 'text-cyan-600', bgColor: 'bg-cyan-100' },
        0: { name: 'Equal Split', icon: 'Users', color: 'text-blue-600', bgColor: 'bg-blue-100' },
        1: { name: 'By Job Role', icon: 'PieChart', color: 'text-purple-600', bgColor: 'bg-purple-100' },
        2: { name: 'Job Role Score', icon: 'Trophy', color: 'text-amber-600', bgColor: 'bg-amber-100' },
   3: { name: 'By Department', icon: 'Building2', color: 'text-emerald-600', bgColor: 'bg-emerald-100' },
        4: { name: 'Hours & Job Role', icon: 'Clock', color: 'text-cyan-600', bgColor: 'bg-cyan-100' },
    };
    return typeMap[type] || { name: 'Custom', icon: 'Settings', color: 'text-gray-600', bgColor: 'bg-gray-100' };
};

const DistributionLab = () => {
    const { currentLocation } = useLocationContext();
 const { selectedTenant, needsTenantSelection, isGlobalAdmin } = useTenantContext();
    const [selectedRuleId, setSelectedRuleId] = useState(null);
    const [selectedRecordId, setSelectedRecordId] = useState(null);
    const [employeeAmounts, setEmployeeAmounts] = useState({});
    const [calculatedDistribution, setCalculatedDistribution] = useState([]);
    const [notification, setNotification] = useState(null);
    const [isCalculating, setIsCalculating] = useState(false);

    const shouldFetchData = !isGlobalAdmin || selectedTenant;

    const { employees: apiEmployees, loading: employeesLoading } = useEmployees({
        autoFetch: shouldFetchData,
        troncEligibleOnly: true
    });
    const { rules, loading: rulesLoading } = useDistributionRules({
        activeOnly: false,
        autoFetch: shouldFetchData
    });
    const { previewDistribution, loading: previewLoading } = useDistributionRuleMutations();
    const { records: troncRecords, loading: troncLoading } = useTroncRecords({
        autoFetch: shouldFetchData
    });

    const loading = employeesLoading || rulesLoading || troncLoading;

    const last30DaysRecords = useMemo(() => {
        if (!troncRecords) return [];
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
     return troncRecords
   .filter(r => {
   const recordDate = new Date(r.periodStart);
          const matchesLocation = !currentLocation || r.locationId === currentLocation.id;
    return recordDate >= thirtyDaysAgo && matchesLocation;
       })
        .sort((a, b) => new Date(b.periodStart) - new Date(a.periodStart))
         .slice(0, 30);
    }, [troncRecords, currentLocation?.id]);

    const selectedRecord = useMemo(() => {
        if (!selectedRecordId || !last30DaysRecords) return null;
        return last30DaysRecords.find(r => r.id === selectedRecordId);
    }, [selectedRecordId, last30DaysRecords]);

    const testAmount = selectedRecord?.amount || 0;

    const locationFilteredEmployees = currentLocation
        ? (apiEmployees || []).filter(emp => emp.locationId === currentLocation.id)
    : (apiEmployees || []);

    const eligibleEmployees = useMemo(() => {
        return locationFilteredEmployees
    .filter(emp => emp.isEligibleForTronc && emp.isActive)
       .map(emp => ({
       id: emp.id,
        name: emp.fullName || `${emp.firstName || ''} ${emp.lastName || ''}`.trim(),
        role: emp.jobTitle || emp.primaryJobRoleName || 'Unassigned',
             jobRoleId: emp.primaryJobRoleId,
    primaryJobRoleId: emp.primaryJobRoleId,
        department: emp.department,
      avatar: emp.photoUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(emp.firstName || '')}+${encodeURIComponent(emp.lastName || '')}&background=1a365d&color=fff`,
        hoursWorked: emp.contractedHoursPerWeek || 40,
                troncScore: emp.troncScore || emp.primaryJobRole?.troncPoints || 1,
     calculatedAmount: 0,
    }));
    }, [locationFilteredEmployees]);

const selectedRule = useMemo(() => {
        if (!selectedRuleId || !rules) return null;
        return rules.find(r => r.id === selectedRuleId);
    }, [selectedRuleId, rules]);

    useEffect(() => {
        setEmployeeAmounts({});
        setCalculatedDistribution([]);
        setSelectedRecordId(null);
    }, [currentLocation?.id]);

    // Auto-calculate when rule and record are selected
    useEffect(() => {
        if (selectedRuleId && selectedRecordId && testAmount > 0 && eligibleEmployees.length > 0) {
     runCalculation();
    } else {
   setCalculatedDistribution([]);
            setEmployeeAmounts({});
        }
    }, [selectedRuleId, selectedRecordId, testAmount, eligibleEmployees.length]);

    const showNotification = (message, type = 'success') => {
        setNotification({ message, type });
   setTimeout(() => setNotification(null), 5000);
    };

    const calculateLocalDistribution = () => {
   const amounts = {};
        
        if (selectedRule?.type === 'Equal' || selectedRule?.type === 0) {
      // Equal distribution
  const equalAmount = Math.round((testAmount / eligibleEmployees.length) * 100) / 100;
            let remaining = testAmount;
   eligibleEmployees.forEach((emp, index) => {
                if (index === eligibleEmployees.length - 1) {
              amounts[emp.id] = remaining;
      } else {
           amounts[emp.id] = equalAmount;
    remaining -= equalAmount;
  }
         });
        } else if (selectedRule?.type === 'ByJobRole' || selectedRule?.type === 1) {
            // By Job Role distribution
       if (!selectedRule.allocations || selectedRule.allocations.length === 0) {
       showNotification('Rule has no allocations defined', 'error');
      return;
            }
        
         const employeesByRole = {};
  eligibleEmployees.forEach(emp => {
            const roleId = emp.primaryJobRoleId || emp.jobRoleId;
           if (!roleId) return;
    if (!employeesByRole[roleId]) {
      employeesByRole[roleId] = [];
        }
 employeesByRole[roleId].push(emp);
 });
            
 selectedRule.allocations.forEach(allocation => {
      const roleEmployees = employeesByRole[allocation.jobRoleId] || [];
        if (roleEmployees.length === 0) return;
            
                const roleAmount = Math.round((testAmount * allocation.percentage / 100) * 100) / 100;
       const amountPerEmployee = Math.round((roleAmount / roleEmployees.length) * 100) / 100;
    let roleRemaining = roleAmount;
     
       roleEmployees.forEach((emp, index) => {
      if (index === roleEmployees.length - 1) {
  amounts[emp.id] = roleRemaining;
     } else {
   amounts[emp.id] = amountPerEmployee;
       roleRemaining -= amountPerEmployee;
       }
     });
});
      } else if (selectedRule?.type === 'ByDepartment' || selectedRule?.type === 3) {
            // By Department distribution
            if (!selectedRule.allocations || selectedRule.allocations.length === 0) {
      showNotification('Rule has no allocations defined', 'error');
return;
            }
 
      // Group employees by department
     const employeesByDept = {};
       eligibleEmployees.forEach(emp => {
  const dept = emp.department || 'FOH'; // Default to FOH if no department
     if (!employeesByDept[dept]) {
          employeesByDept[dept] = [];
       }
                employeesByDept[dept].push(emp);
            });
       
     selectedRule.allocations.forEach(allocation => {
       if (!allocation.department) return;
                
                const deptEmployees = employeesByDept[allocation.department] || [];
    if (deptEmployees.length === 0) return;
    
        const deptAmount = Math.round((testAmount * allocation.percentage / 100) * 100) / 100;
     const amountPerEmployee = Math.round((deptAmount / deptEmployees.length) * 100) / 100;
         let deptRemaining = deptAmount;
   
     deptEmployees.forEach((emp, index) => {
          if (index === deptEmployees.length - 1) {
       amounts[emp.id] = deptRemaining;
     } else {
            amounts[emp.id] = amountPerEmployee;
           deptRemaining -= amountPerEmployee;
        }
    });
        });
        } else if (selectedRule?.type === 'ByJobRoleScore' || selectedRule?.type === 2) {
            // By Job Role Score distribution
        // Calculate total score
            let totalScore = 0;
      const employeeScores = {};
            
            eligibleEmployees.forEach(emp => {
                const score = emp.troncScore || 1;
        employeeScores[emp.id] = score;
          totalScore += score;
     });
      
       if (totalScore === 0) {
          // Fallback to equal distribution
     const equalAmount = Math.round((testAmount / eligibleEmployees.length) * 100) / 100;
                let remaining = testAmount;
        eligibleEmployees.forEach((emp, index) => {
    if (index === eligibleEmployees.length - 1) {
     amounts[emp.id] = remaining;
                } else {
    amounts[emp.id] = equalAmount;
                   remaining -= equalAmount;
    }
       });
   } else {
    // Distribute based on scores
         let distributed = 0;
         eligibleEmployees.forEach((emp, index) => {
     const empScore = employeeScores[emp.id];
        let amount;
       
            if (index === eligibleEmployees.length - 1) {
 // Last employee gets remainder
    amount = testAmount - distributed;
   } else {
        amount = Math.round((empScore / totalScore) * testAmount * 100) / 100;
     distributed += amount;
     }
     
          amounts[emp.id] = amount;
    });
     }
        } else if (selectedRule?.type === 'ByHoursAndJobRole' || selectedRule?.type === 4) {
     // By Hours and Job Role distribution
     // Calculate total weighted score (hours � role score)
 let totalWeight = 0;
          const employeeWeights = {};
    
      eligibleEmployees.forEach(emp => {
          const hours = emp.hoursWorked || 40;
      const score = emp.troncScore || 1;
          const weight = hours * score;
      employeeWeights[emp.id] = weight;
totalWeight += weight;
            });
        
       if (totalWeight === 0) {
       // Fallback to equal distribution
         const equalAmount = Math.round((testAmount / eligibleEmployees.length) * 100) / 100;
       let remaining = testAmount;
  eligibleEmployees.forEach((emp, index) => {
              if (index === eligibleEmployees.length - 1) {
       amounts[emp.id] = remaining;
 } else {
       amounts[emp.id] = equalAmount;
     remaining -= equalAmount;
      }
     });
            } else {
      // Distribute based on weighted scores
     let distributed = 0;
            eligibleEmployees.forEach((emp, index) => {
const empWeight = employeeWeights[emp.id];
     let amount;
           
   if (index === eligibleEmployees.length - 1) {
   // Last employee gets remainder
  amount = testAmount - distributed;
    } else {
 amount = Math.round((empWeight / totalWeight) * testAmount * 100) / 100;
       distributed += amount;
            }
       
       amounts[emp.id] = amount;
     });
   }
        }
   
        setEmployeeAmounts(amounts);
    };

    const runCalculation = async () => {
    if (!selectedRule || !selectedRecord || testAmount <= 0 || eligibleEmployees.length === 0) {
     setCalculatedDistribution([]);
            setEmployeeAmounts({});
   return;
}
    setIsCalculating(true);
        try {
            const result = await previewDistribution(selectedRule.id, testAmount, currentLocation?.id || null);
            if (result.success && result.data?.distributions) {
  setCalculatedDistribution(result.data.distributions);
           const amounts = {};
     result.data.distributions.forEach(d => {
   amounts[d.employeeId] = d.amount;
});
    setEmployeeAmounts(amounts);
            } else {
         showNotification('Failed to calculate distribution: ' + (result.message || 'Unknown error'), 'error');
  calculateLocalDistribution();
 }
        } catch (err) {
            console.error('Preview failed:', err);
            showNotification('Distribution preview failed. Using local calculation.', 'warning');
        calculateLocalDistribution();
        } finally {
 setIsCalculating(false);
     }
    };

    const strategyBreakdown = useMemo(() => {
        if (!selectedRule || testAmount === 0 || eligibleEmployees.length === 0) return null;
        const ruleType = selectedRule.type;

      if (ruleType === 'Equal' || ruleType === 0) {
 const perEmployee = testAmount / eligibleEmployees.length;
         return {
    type: 'equal',
       description: 'Total TRONC divided equally among all eligible employees',
         formula: `${formatCurrency(testAmount)} / ${eligibleEmployees.length} employees`,
        result: formatCurrency(perEmployee),
           items: [{ label: 'Per Employee', value: formatCurrency(perEmployee), count: eligibleEmployees.length, percentage: 100 }]
            };
        }

        if (ruleType === 'ByJobRole' || ruleType === 1) {
 const items = (selectedRule.allocations || []).map(allocation => {
        const roleEmployees = eligibleEmployees.filter(e => e.jobRoleId === allocation.jobRoleId || e.primaryJobRoleId === allocation.jobRoleId);
          const roleAmount = testAmount * (allocation.percentage / 100);
         const perEmployee = roleEmployees.length > 0 ? roleAmount / roleEmployees.length : 0;
   return { label: allocation.jobRoleName || 'Unknown Role', percentage: allocation.percentage, value: formatCurrency(roleAmount), count: roleEmployees.length, perEmployee: formatCurrency(perEmployee) };
   });
  return { type: 'byJobRole', description: 'TRONC split by configured percentage per job role', formula: 'Role % x Total TRONC / Employees in Role', items };
     }

        if (ruleType === 'ByDepartment' || ruleType === 3) {
const items = (selectedRule.allocations || []).map(allocation => {
 const deptEmployees = eligibleEmployees.filter(e => e.department === allocation.department);
    const deptAmount = testAmount * (allocation.percentage / 100);
                const perEmployee = deptEmployees.length > 0 ? deptAmount / deptEmployees.length : 0;
             return { label: allocation.department || 'Unknown Department', percentage: allocation.percentage, value: formatCurrency(deptAmount), count: deptEmployees.length, perEmployee: formatCurrency(perEmployee) };
            });
      return { type: 'byDepartment', description: 'TRONC split by configured percentage per department', formula: 'Department % x Total TRONC / Employees in Department', items };
        }

        if (ruleType === 'ByJobRoleScore' || ruleType === 2) {
            const totalScore = eligibleEmployees.reduce((sum, emp) => sum + (emp.troncScore || 1), 0);
  const items = [];
         const roleGroups = {};
   eligibleEmployees.forEach(emp => {
   const roleName = emp.role || 'Unassigned';
          if (!roleGroups[roleName]) roleGroups[roleName] = { employees: [], totalScore: 0 };
      roleGroups[roleName].employees.push(emp);
      roleGroups[roleName].totalScore += emp.troncScore || 1;
      });
  Object.entries(roleGroups).forEach(([roleName, data]) => {
          const rolePercentage = (data.totalScore / totalScore) * 100;
                const roleAmount = testAmount * (rolePercentage / 100);
         const perEmployee = roleAmount / data.employees.length;
 items.push({ label: roleName, percentage: Math.round(rolePercentage * 10) / 10, value: formatCurrency(roleAmount), count: data.employees.length, perEmployee: formatCurrency(perEmployee), score: data.totalScore });
          });
            return { type: 'byScore', description: "TRONC split based on each role's Tronc Points score", formula: 'Employee Score / Total Score x Total TRONC', totalScore, items: items.sort((a, b) => b.percentage - a.percentage) };
        }

  if (ruleType === 'ByHoursAndJobRole' || ruleType === 4) {
       const employeeWeights = eligibleEmployees.map(emp => ({ ...emp, weight: (emp.hoursWorked || 40) * (emp.troncScore || 1) }));
         const totalWeight = employeeWeights.reduce((sum, emp) => sum + emp.weight, 0);
const roleGroups = {};
       employeeWeights.forEach(emp => {
         const roleName = emp.role || 'Unassigned';
                if (!roleGroups[roleName]) roleGroups[roleName] = { employees: [], totalWeight: 0 };
roleGroups[roleName].employees.push(emp);
           roleGroups[roleName].totalWeight += emp.weight;
         });
        const items = Object.entries(roleGroups).map(([roleName, data]) => {
            const rolePercentage = (data.totalWeight / totalWeight) * 100;
            const roleAmount = testAmount * (rolePercentage / 100);
         const perEmployee = roleAmount / data.employees.length;
   return { label: roleName, percentage: Math.round(rolePercentage * 10) / 10, value: formatCurrency(roleAmount), count: data.employees.length, perEmployee: formatCurrency(perEmployee), weight: Math.round(data.totalWeight) };
    });
      return { type: 'byHoursAndRole', description: 'Hours worked x Job role score determines share', formula: '(Hours x Score) / Total Weight x Total TRONC', totalWeight: Math.round(totalWeight), items: items.sort((a, b) => b.percentage - a.percentage) };
        }
        return null;
    }, [selectedRule, testAmount, eligibleEmployees]);

const breadcrumbs = [
        { label: 'Dashboard', path: '/dashboard-overview' },
     { label: 'Settings', path: '/settings/distribution-rules' },
 { label: 'Distribution Lab', path: '/distribution-lab' }
    ];

    const getTotalDistributed = () => Object.values(employeeAmounts).reduce((sum, amt) => sum + (amt || 0), 0);
    const getAverageAmount = () => eligibleEmployees.length > 0 ? getTotalDistributed() / eligibleEmployees.length : 0;
    const employeesWithAmounts = eligibleEmployees.map(emp => ({ ...emp, calculatedAmount: employeeAmounts[emp.id] || 0 }));

    const handleExport = () => {
     if (!selectedRule) {
   showNotification('Please select a rule first', 'warning');
       return;
 }
 if (!selectedRecord) {
  showNotification('Please select a TRONC record first', 'warning');
         return;
        }
    const headers = ['Employee', 'Role', 'Department', 'Amount', 'Percentage'];
   const rows = employeesWithAmounts.map(emp => [
     emp.name,
      emp.role,
            emp.department || 'N/A',
          formatCurrency(emp.calculatedAmount || 0),
     `${((emp.calculatedAmount / testAmount) * 100 || 0).toFixed(2)}%`
        ]);
        const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
     const blob = new Blob([csv], { type: 'text/csv' });
 const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
      a.href = url;
    a.download = `distribution-lab-${selectedRule.name.replace(/\s+/g, '-').toLowerCase()}-${formatDate(selectedRecord.periodStart).replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
 showNotification('Distribution exported to CSV');
    };

    if (isGlobalAdmin && needsTenantSelection) {
        return (
        <>
    <Helmet><title>Distribution Lab - Rocket Tronc</title></Helmet>
                <PageLayout title="Distribution Lab" description="Test distribution rules without affecting real data." breadcrumbs={breadcrumbs}>
     <div className="card p-12 text-center">
       <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
             <Icon name="Building2" size={40} className="text-primary" />
  </div>
   <h2 className="text-2xl font-bold mb-3">Select a Tenant</h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">As a Global Administrator, please select a tenant from the sidebar to test their distribution rules.</p>
     <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
      <Icon name="ArrowLeft" size={16} />
          <span>Use the tenant selector in the sidebar</span>
     </div>
                </div>
              </PageLayout>
            </>
      );
    }

    const typeInfo = selectedRule ? getDistributionTypeInfo(selectedRule.type) : null;

    return (
      <>
 <Helmet>
             <title>Distribution Lab - {selectedTenant?.name || 'Rocket Tronc'}</title>
           <meta name="description" content="Test distribution rules without affecting real data" />
            </Helmet>
 <PageLayout title="Distribution Lab" description="Test distribution rules with real TRONC data from the last 30 days. Select a rule and a TRONC record to simulate how it would be distributed." breadcrumbs={breadcrumbs}>
  {notification && (
   <div className={`mb-6 alert ${notification.type === 'error' ? 'alert-error' : notification.type === 'warning' ? 'alert-warning' : 'alert-success'}`}>
          <Icon name={notification.type === 'error' ? 'AlertCircle' : notification.type === 'warning' ? 'AlertTriangle' : 'CheckCircle'} size={20} />
       <span>{notification.message}</span>
 <button onClick={() => setNotification(null)} className="ml-auto"><Icon name="X" size={16} /></button>
   </div>
        )}

     <div className="mb-6 bg-warning/10 border border-warning/30 rounded-lg p-4">
       <div className="flex items-start gap-3">
        <Icon name="FlaskConical" size={24} className="text-warning shrink-0" />
     <div>
     <h3 className="font-semibold text-warning mb-1">Testing Environment</h3>
     <p className="text-sm text-muted-foreground">This is a testing sandbox using real TRONC data. Select a TRONC record from the last 30 days to see how different distribution rules would allocate the amounts. Results are simulations only and will not be saved or applied.</p>
           </div>
           </div>
    </div>

{loading && (
  <div className="card p-8 text-center mb-6">
      <div className="spinner mx-auto mb-4" />
       <p className="text-muted-foreground">Loading data...</p>
               </div>
              )}

     {!loading && (
       <div className="mb-6 card p-6 border-2 border-primary/20 bg-primary/5">
   <div className="flex items-center gap-4 mb-4">
    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
       <Icon name="Settings2" size={24} className="text-primary" />
   </div>
  <div>
    <h3 className="font-semibold text-lg">Test Configuration</h3>
       <p className="text-sm text-muted-foreground">Select a rule and a TRONC record to simulate distribution</p>
     </div>
       </div>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
     <div>
<label className="label text-sm mb-1">Distribution Rule</label>
 <select value={selectedRuleId || ''} onChange={(e) => { setSelectedRuleId(e.target.value || null); setEmployeeAmounts({}); setCalculatedDistribution([]); }} className="input w-full">
   <option value="">Select a rule...</option>
           {rules?.map(rule => (<option key={rule.id} value={rule.id}>{rule.name} {rule.isDraft ? '(Draft)' : rule.isActive ? '(Active)' : ''}</option>))}
   </select>
        </div>
   <div>
  <label className="label text-sm mb-1">TRONC Record (Last 30 Days)</label>
   <select 
       value={selectedRecordId || ''} 
       onChange={(e) => { 
      setSelectedRecordId(e.target.value || null); 
    setEmployeeAmounts({}); 
       setCalculatedDistribution([]); 
  }} 
 className="input w-full"
          disabled={last30DaysRecords.length === 0}
         >
            <option value="">Select a TRONC record...</option>
        {last30DaysRecords.map(record => (
      <option key={record.id} value={record.id}>
            {formatDate(record.periodStart)} - {formatCurrency(record.amount)}
  {record.locationName && ` (${record.locationName})`}
  </option>
     ))}
  </select>
        {last30DaysRecords.length === 0 && (
 <p className="text-xs text-muted-foreground mt-1">
     No TRONC records found in the last 30 days
        </p>
        )}
   </div>
    </div>
       {(isCalculating || previewLoading) && (
  <div className="mt-4 p-3 bg-primary/5 rounded-lg border border-primary/20 flex items-center gap-3">
   <div className="spinner spinner-sm" />
      <span className="text-sm text-muted-foreground">Calculating distribution preview...</span>
        </div>
       )}
         {selectedRecord && (
            <div className="mt-4 p-3 bg-muted/50 rounded-lg border border-border">
            <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2 text-muted-foreground">
    <Icon name="Calendar" size={14} />
        <span>Selected Record:</span>
            </div>
          <div className="flex items-center gap-4">
      <div>
      <span className="text-muted-foreground">Date: </span>
  <strong>{formatDate(selectedRecord.periodStart)}</strong>
       </div>
          <div>
   <span className="text-muted-foreground">Amount: </span>
    <strong className="text-primary">{formatCurrency(selectedRecord.amount)}</strong>
            </div>
       {selectedRecord.cashTips > 0 && (
            <div>
          <span className="text-muted-foreground">Cash: </span>
  <span>{formatCurrency(selectedRecord.cashTips)}</span>
 </div>
          )}
        {selectedRecord.cardTips > 0 && (
      <div>
     <span className="text-muted-foreground">Card: </span>
        <span>{formatCurrency(selectedRecord.cardTips)}</span>
     </div>
         )}
         {selectedRecord.serviceCharge > 0 && (
        <div>
     <span className="text-muted-foreground">Service: </span>
                    <span>{formatCurrency(selectedRecord.serviceCharge)}</span>
   </div>
          )}
  </div>
</div>
     </div>
        )}
         {currentLocation && (
<div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
      <Icon name="MapPin" size={14} />
     <span>Testing with employees from: <strong>{currentLocation.name}</strong></span>
     <span className="text-xs">({eligibleEmployees.length} eligible employees)</span>
             </div>
)}
          </div>
     )}

    {!loading && selectedRule && (
            <div className="card mb-6 border-2 border-primary/20 overflow-hidden">
       <div className="p-6 flex items-start justify-between">
           <div className="flex items-start gap-4">
     <div className={`w-14 h-14 rounded-xl ${typeInfo?.bgColor || 'bg-primary/10'} flex items-center justify-center`}>
   <Icon name={typeInfo?.icon || 'Settings'} size={28} className={typeInfo?.color || 'text-primary'} />
     </div>
             <div>
        <div className="flex items-center gap-3 mb-1">
        <h2 className="text-xl font-heading font-bold">{selectedRule.name}</h2>
            {selectedRule.isDraft && (<span className="badge badge-warning">Draft</span>)}
 {selectedRule.isActive && !selectedRule.isDraft && (<span className="badge badge-success">Active</span>)}
         {!selectedRule.isActive && !selectedRule.isDraft && (<span className="badge badge-neutral">Inactive</span>)}
             </div>
        <p className="text-sm text-muted-foreground">
     <span className="font-medium">{typeInfo?.name}</span>
 {selectedRule.description && (<span> &bull; {selectedRule.description}</span>)}
             </p>
         </div>
        </div>
       <a href="/settings/distribution-rules" className="btn btn-outline btn-sm"><Icon name="Settings" size={16} />Edit Rules</a>
       </div>

    {strategyBreakdown && Object.keys(employeeAmounts).length > 0 && (
   <div className="border-t border-border p-6 bg-muted/30">
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          <Icon name="Calculator" size={18} className="text-primary" />
        <h3 className="font-semibold">Distribution Strategy</h3>
        </div>
  <p className="text-sm text-muted-foreground">{strategyBreakdown.description}</p>
            <p className="text-xs text-muted-foreground mt-1 font-mono bg-muted px-2 py-1 rounded inline-block">{strategyBreakdown.formula}</p>
              </div>

            {strategyBreakdown.type === 'equal' ? (
     <div className="bg-card rounded-lg border border-border p-6 text-center">
            <div className="text-4xl font-bold text-primary mb-2">{strategyBreakdown.result}</div>
     <p className="text-muted-foreground">per employee</p>
           <p className="text-sm text-muted-foreground mt-2">{formatCurrency(testAmount)} / {eligibleEmployees.length} = {strategyBreakdown.result}</p>
     </div>
      ) : (
   <div className="space-y-2">
     <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide px-3 pb-2">
      <div className="col-span-4">{strategyBreakdown.type === 'byDepartment' ? 'Department' : 'Role'}</div>
    <div className="col-span-2 text-right">Share</div>
      <div className="col-span-2 text-right">Amount</div>
  <div className="col-span-2 text-right">Staff</div>
                <div className="col-span-2 text-right">Per Person</div>
               </div>
  {strategyBreakdown.items.map((item, index) => (
              <div key={index} className="grid grid-cols-12 gap-2 items-center p-3 bg-card rounded-lg border border-border hover:border-primary/30 transition-colors">
        <div className="col-span-4 flex items-center gap-2">
        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: `hsl(${(index * 60) % 360}, 70%, 50%)` }} />
     <span className="font-medium truncate">{item.label}</span>
                  </div>
       <div className="col-span-2 text-right"><span className="font-bold text-primary">{item.percentage}%</span></div>
             <div className="col-span-2 text-right"><span className="font-semibold">{item.value}</span></div>
              <div className="col-span-2 text-right text-muted-foreground">{item.count}</div>
    <div className="col-span-2 text-right"><span className="font-medium text-success">{item.perEmployee}</span></div>
        </div>
   ))}
     <div className="grid grid-cols-12 gap-2 items-center p-3 bg-primary/5 rounded-lg border-2 border-primary/20 mt-2">
            <div className="col-span-4 font-bold">Total</div>
      <div className="col-span-2 text-right font-bold">100%</div>
              <div className="col-span-2 text-right font-bold">{formatCurrency(testAmount)}</div>
              <div className="col-span-2 text-right text-muted-foreground">{eligibleEmployees.length}</div>
               <div className="col-span-2 text-right font-bold text-success">{formatCurrency(getAverageAmount())}<span className="text-xs text-muted-foreground ml-1">avg</span></div>
          </div>
     </div>
        )}

   {(strategyBreakdown.type === 'byScore' || strategyBreakdown.type === 'byHoursAndRole') && (
  <div className="mt-4 text-xs text-muted-foreground">
    {strategyBreakdown.type === 'byScore' && (<span>Total Tronc Points: <strong>{strategyBreakdown.totalScore}</strong></span>)}
   {strategyBreakdown.type === 'byHoursAndRole' && (<span>Total Weighted Score: <strong>{strategyBreakdown.totalWeight}</strong> (hours x role score)</span>)}
  </div>
          )}
          </div>
   )}
     </div>
      )}

    {!loading && !selectedRule && (
             <div className="card mb-6 p-8 text-center">
     <div className="w-16 h-16 rounded-full bg-info/10 flex items-center justify-center mx-auto mb-4">
        <Icon name="FlaskConical" size={32} className="text-info" />
 </div>
   <h3 className="text-lg font-semibold mb-2">Select a Rule to Test</h3>
      <p className="text-muted-foreground mb-4 max-w-md mx-auto">Choose a distribution rule from the dropdown above to simulate how it would distribute TRONC among employees.</p>
                  <a href="/settings/distribution-rules" className="btn btn-outline"><Icon name="Plus" size={18} />Create New Rule</a>
 </div>
            )}

           {!loading && selectedRule && Object.keys(employeeAmounts).length > 0 && (
        <div className="space-y-6">
                <CalculationSummary totalAmount={testAmount} recipientCount={eligibleEmployees.length} averageAmount={getAverageAmount()} remainingBalance={testAmount - getTotalDistributed()} />
           <PageSection title="Simulated Employee Allocations">
 <EmployeeDistributionList employees={employeesWithAmounts} onAmountChange={() => { }} onOverrideNotesChange={() => { }} isProcessing={false} totalAmount={testAmount} readOnly={true} />
   </PageSection>
     <div className="card p-6 border-2 border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
       <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
   <Icon name="Download" size={20} className="text-muted-foreground" />
    </div>
    <div>
  <h3 className="font-semibold">Export Results</h3>
          <p className="text-sm text-muted-foreground">Download the simulated distribution as CSV</p>
      </div>
    </div>
      <button className="btn btn-outline" onClick={handleExport}><Icon name="Download" size={18} />Export to CSV</button>
    </div>
 </div>
       </div>
     )}

       {!loading && selectedRule && eligibleEmployees.length === 0 && (
          <div className="card p-8 text-center">
         <div className="empty-state-icon mx-auto mb-4"><Icon name="Users" size={32} /></div>
             <h3 className="text-lg font-semibold mb-2">No eligible employees</h3>
          <p className="text-muted-foreground max-w-md mx-auto">No TRONC-eligible employees found{currentLocation ? ` at ${currentLocation.name}` : ''}. Add employees and mark them as eligible for TRONC to test distributions.</p>
       </div>
       )}

    {!loading && last30DaysRecords.length === 0 && (
 <div className="card mb-6 p-8 text-center">
    <div className="w-16 h-16 rounded-full bg-warning/10 flex items-center justify-center mx-auto mb-4">
  <Icon name="AlertCircle" size={32} className="text-warning" />
  </div>
  <h3 className="text-lg font-semibold mb-2">No TRONC Records Available</h3>
     <p className="text-muted-foreground max-w-md mx-auto">
     No TRONC records found in the last 30 days{currentLocation ? ` for ${currentLocation.name}` : ''}. 
    Add TRONC records to test distribution rules.
      </p>
         <a href="/daily-takings-and-tronc" className="btn btn-primary mt-4">
<Icon name="Plus" size={18} />
    Go to Daily Takings
    </a>
      </div>
 )}

          </PageLayout>
        </>
    );
};

export default DistributionLab;